import { lazy, Suspense } from 'react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Button, Card, CardActionArea, Chip, CircularProgress, Collapse, Drawer, Paper, Stack, SxProps, Table, TableBody, TableCell, TableContainer, TableRow, Typography } from '@mui/material'
import { ExtraSmallButton, NonInteractiveTooltip } from '../custom'
import { mergeSideBarBottomButtonSx } from '../sideBarRight/sideBarBottomIconButtonSx'
import {
    createOverlayDrawerOnClose,
    sideBarOverlayDrawerPaperProps,
    sideBarOverlayDrawerProps,
    useSideBarOverlayDrawerEscape
} from '../sideBarRight/sideBarOverlayDrawerProps'
import CalendarIcon from '../../assets/icons/CalendarIcon'
import { CompanyEventsAndActions } from '../../proto/generated/calendar-event'
import { CompanyEventProto, PurposeType } from '../../proto/generated/company_event'
import { CorporateAction, Type } from '../../proto/generated/corporate-action'
import { useDispatch, useSelector } from 'react-redux'
import { selectCalendarWeekCardsExpanded, selectCurrentTheme } from '../../selectors/common'
import commonActionTypes from '../../actionTypes/common.actionTypes'
import ClearIcon from '../../assets/icons/ClearIcon'
import ThinArrowDownIcon from '../../assets/icons/ThinArrowDownIcon'
import ThinArrowUpIcon from '../../assets/icons/ThinArrowUpIcon'
const WeekPicker = lazy(() => import('../date/WeekPicker'))
import dayjs, { Dayjs } from 'dayjs'
import { getCalendarEvents } from '../../services/apis/calendars'
import EmptySearch from '../../assets/icons/EmptySearch'
import { getSecurityKey } from '../../util/security'
import SymbolTableCell from './SymbolTableCell'
import { Exchange } from '../../proto/generated/common'
import { generatePath } from 'react-router-dom'
import ROUTES from '../../assets/routes'
import { getEndOfWeek, getStartOfWeek } from '../../util/general'
import PurposeTableCell from './PurposeTableCell'
import CalendarWeekCardsSkeleton from './CalendarWeekCardsSkeleton'

type FilterCategory = 'Board Meeting' | 'Earnings' | 'Dividend' | 'Bonus' | 'Split' | 'Rights' | 'Others'

const displayedFilters: FilterCategory[] = ['Board Meeting', 'Earnings', 'Dividend', 'Bonus', 'Split', 'Rights', 'Others']

const categoryMap: Record<Type, FilterCategory> = {
    [Type.BONUS]: 'Bonus',
    [Type.SPLIT]: 'Split',
    [Type.RIGHTS]: 'Rights',
    [Type.INTERIM_DIVIDEND]: 'Dividend',
    [Type.FINAL_DIVIDEND]: 'Dividend',
    [Type.SPECIAL_DIVIDEND]: 'Dividend',
    [Type.TYPE_UNKNOWN]: 'Others',
    [Type.UNRECOGNIZED]: 'Others'
}

const cardDisplayFilters: FilterCategory[] = displayedFilters.filter(
    (value) => value !== 'Others' && value !== 'Earnings'
)

const boardMeetingPurposes: PurposeType[] = [
    PurposeType.DIVIDEND,
    PurposeType.BOARD_MEETING,
    PurposeType.FUND_RAISING,
    PurposeType.SPLIT,
    PurposeType.BUYBACK,
    PurposeType.BONUS,
    PurposeType.OTHER_BUSINESS_MATTERS,
    PurposeType.VOLUNTARY_DELISTING
]

const isIntimationOnlyEvent = (event: CompanyEventProto) => {
    const details = event.details?.toLowerCase() || ''
    return details.includes('intimation') && event.purpose?.length === 1
}

const getEventCategory = (event: CompanyEventProto): FilterCategory | null => {
    if (isIntimationOnlyEvent(event)) return null
    if (event.purpose?.includes(PurposeType.FINANCIAL_RESULTS)) return 'Earnings'
    if (
        !event.purpose.includes(PurposeType.FINANCIAL_RESULTS) &&
        event.purpose?.some((purpose) => boardMeetingPurposes.includes(purpose))
    ) {
        return 'Board Meeting'
    }
    return null
}

const matchesFilter = (
    type: 'event' | 'action',
    item: CompanyEventProto | CorporateAction,
    selectedFilter: FilterCategory
): boolean => {
    if (type === 'event') {
        const category = getEventCategory(item as CompanyEventProto)
        return category === selectedFilter
    }
    return categoryMap[(item as CorporateAction).type] === selectedFilter
}

const getDayCounts = (dayData: CompanyEventsAndActions | undefined): Record<FilterCategory, number> => {
    const counts = Object.fromEntries(displayedFilters.map((f) => [f, 0])) as Record<FilterCategory, number>
    if (!dayData) return counts

    dayData.companyEvents.forEach((event) => {
        const category = getEventCategory(event)
        if (category) counts[category]++
    })
    dayData.corporateActions.forEach((action) => {
        counts[categoryMap[action.type]]++
    })
    return counts
}

const getTopCategoryCounts = (dayData: CompanyEventsAndActions | undefined, limit = 3) => {
    const counts = getDayCounts(dayData)
    return cardDisplayFilters
        .map((category) => ({ category, count: counts[category] }))
        .filter(({ count }) => count > 0)
        .sort((a, b) => b.count - a.count)
        .slice(0, limit)
}

const dateCellSx: SxProps = {
    padding: '8px'
}

const tableCellProps: SxProps = {
    padding: '8px'
}

const tableRowProps: SxProps = {
    '&:hover': {
        backgroundColor: (theme: any) => theme.palette.action.hover
    }
}

const getRowScrollTop = (row: HTMLElement, scrollContainer: HTMLElement): number => {
    const isSticky = getComputedStyle(row).position === 'sticky'
    if (isSticky) {
        row.style.position = 'relative'
        row.style.top = 'auto'
    }

    let offset = 0
    let current: HTMLElement | null = row
    while (current && current !== scrollContainer) {
        offset += current.offsetTop
        current = current.offsetParent as HTMLElement | null
    }

    if (current !== scrollContainer) {
        const rowRect = row.getBoundingClientRect()
        const containerRect = scrollContainer.getBoundingClientRect()
        offset = rowRect.top - containerRect.top + scrollContainer.scrollTop
    }

    if (isSticky) {
        row.style.position = ''
        row.style.top = ''
    }
    return offset
}

const scrollCalendarDateRowIntoView = (
    row: HTMLElement,
    scrollContainer: HTMLElement | null,
    smooth: boolean
) => {
    const behavior = smooth ? 'smooth' : 'auto'
    if (!scrollContainer) {
        row.scrollIntoView({ behavior, block: 'start' })
        return
    }
    scrollContainer.scrollTo({ top: getRowScrollTop(row, scrollContainer), behavior })
}

const typeLabels: Record<Type, string> = {
    [Type.TYPE_UNKNOWN]: 'Unknown',
    [Type.BONUS]: 'Bonus',
    [Type.SPLIT]: 'Split',
    [Type.RIGHTS]: 'Rights',
    [Type.INTERIM_DIVIDEND]: 'Interim',
    [Type.FINAL_DIVIDEND]: 'Final',
    [Type.SPECIAL_DIVIDEND]: 'Special',
    [Type.UNRECOGNIZED]: 'Unrecognized'
}

export function Calendars() {
    const dispatch = useDispatch()
    const [open, setOpen] = useState(false)
    const [data, setData] = useState<{ [key: string]: CompanyEventsAndActions }>()
    const [loading, setLoading] = useState(true)
    const loadingRef = useRef(false)
    const darkTheme = useSelector(selectCurrentTheme)
    const [weekRange, setWeekRange] = useState<{ start: Dayjs; end: Dayjs } | null>(null)
    const [selectedFilter, setSelectedFilter] = useState<FilterCategory>('Board Meeting')
    const [isCurrentWeek, setIsCurrentWeek] = useState<boolean>(false)
    const tableRef = useRef<HTMLDivElement | null>(null)
    const tableContainerRef = useRef<HTMLDivElement | null>(null)
    const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null)
    const weekCardsExpanded = useSelector(selectCalendarWeekCardsExpanded)

    const setCurrentWeekRange = () => {
        const today = dayjs()
        const startOfCurrentWeek = dayjs(getStartOfWeek(today.toDate()))
        const endOfCurrentWeek = dayjs(getEndOfWeek(today.toDate()))
        setWeekRange({ start: startOfCurrentWeek, end: endOfCurrentWeek })
        setSelectedDate(today)
    }

    useEffect(() => {
        if (open) {
            setCurrentWeekRange()
            loadingRef.current = true
            if (tableRef.current) {
                tableRef.current.style.opacity = '0'
            }
            scrollToCurrentWeekIfNeeded(true)
        }
    }, [open])

    useEffect(() => {
        if (!open || !weekRange) return
        setLoading(true)
        loadingRef.current = true
        const fromDate = weekRange.start.format('YYYY-MM-DD')
        const toDate = weekRange.end.format('YYYY-MM-DD')
        getCalendarEvents({ from_date: fromDate, to_date: toDate })
            .then((response) => {
                const newdata: { [key: string]: CompanyEventsAndActions } = {}
                response.calendarEvents.forEach((event) => {
                    newdata[event.date] = event
                })
                setData(newdata)
            })
            .catch((error) => console.error('Error fetching calendar events:', error))
            .finally(() => {
                setLoading(false)
                loadingRef.current = false
            })
    }, [open, weekRange])

    const toggleDrawer = useCallback((state: boolean) => {
        setOpen(state)
    }, [])

    const handleCalendarButtonClick = useCallback(() => {
        setOpen(prev => !prev)
    }, [])

    const handleDrawerClose = useCallback(() => {
        toggleDrawer(false)
    }, [toggleDrawer])

    useSideBarOverlayDrawerEscape(open, handleDrawerClose)

    const handleSymbolClick = useCallback((primaryCode: string, exchange: Exchange) => {
        if (!primaryCode) {
            return null
        }
        const origin = window.location.origin
        return window.open(origin + `${generatePath(ROUTES.symbol, { symbol: getSecurityKey(exchange, primaryCode) })}`, '_blank')
    }, [])

    const handleFilterChange = useCallback((filter: FilterCategory) => {
        setSelectedFilter(filter)
    }, [])

    const handleWeekChange = useCallback((startDate: Dayjs, endDate: Dayjs) => {
        setWeekRange({ start: startDate, end: endDate })
        setIsCurrentWeek(false)
    }, [])

    const scrollToCurrentWeekIfNeeded = (initialLoad = false) => {
        const today = dayjs()
        const startOfThisWeek = dayjs(getStartOfWeek(today.toDate()))
        const endOfThisWeek = dayjs(getEndOfWeek(today.toDate()))

        const isAlreadyCurrentWeek = weekRange?.start.isSame(startOfThisWeek, 'day') && weekRange?.end.isSame(endOfThisWeek, 'day')

        if (isAlreadyCurrentWeek) {
            let i = 0
            const intervalId = setInterval(() => {
                if (loadingRef.current) {
                    i = i + 1
                    if (i === 100) {
                        clearInterval(intervalId)
                        if (initialLoad && tableRef.current) {
                            tableRef.current.style.opacity = '1'
                        }
                    }
                } else {
                    clearInterval(intervalId)
                    const todayRow = document.getElementById(`scrollable-date-${dayjs().format('YYYY-MM-DD')}`)
                    if (todayRow) {
                        scrollCalendarDateRowIntoView(todayRow, tableContainerRef.current, !initialLoad)
                    }
                    if (initialLoad && tableRef.current) {
                        tableRef.current.style.opacity = '1'
                    }
                }
            }, 100)
            return
        }
        loadingRef.current = true
        setCurrentWeekRange()
        setIsCurrentWeek(true)
        let i = 0
        const intervalId = setInterval(() => {
            if (loadingRef.current) {
                i = i + 1
                if (i === 100) {
                    clearInterval(intervalId)
                }
            } else {
                clearInterval(intervalId)
                const el = document.getElementById(`scrollable-date-${dayjs().format('YYYY-MM-DD')}`)
                if (el) {
                    scrollCalendarDateRowIntoView(el, tableContainerRef.current, false)
                }
            }
        }, 100)
    }

    const scrollToSelectedDate = (selected: Dayjs, initialLoad = false) => {
        setSelectedDate(selected)
        const scrollTargetId = `scrollable-date-${selected.format('YYYY-MM-DD')}`
        let i = 0
        const intervalId = setInterval(() => {
            if (loadingRef.current) {
                i = i + 1
                if (i === 100) {
                    clearInterval(intervalId)
                    if (initialLoad && tableRef.current) {
                        tableRef.current.style.opacity = '1'
                    }
                }
            } else {
                clearInterval(intervalId)
                const row = document.getElementById(scrollTargetId)
                if (row) {
                    scrollCalendarDateRowIntoView(row, tableContainerRef.current, !initialLoad)
                }
            }
        }, 100)
    }

    const renderRows = (data: any, selectedFilter: FilterCategory) => {
        if (!data || Object.keys(data).length === 0) {
            return <EmptyRow />
        }
        const rows = []
        let hasRenderedEvents = false

        for (const [date, value] of Object.entries(data)) {
            const { companyEvents, corporateActions } = value as {
                companyEvents: CompanyEventProto[]
                corporateActions: CorporateAction[]
            }

            const merged: { type: 'event' | 'action'; data: CompanyEventProto | CorporateAction }[] = [
                ...companyEvents.map((data) => ({
                    type: 'event' as const,
                    data
                })),
                ...corporateActions.map((data) => ({ type: 'action' as const, data }))
            ]

            const filtered = merged.filter(({ type, data }) => matchesFilter(type, data, selectedFilter))

            if (filtered.length === 0) continue

            hasRenderedEvents = true

            const isEvent = filtered[0].type === 'event'
            const formattedDate = new Date(date).toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric'
            })
            if (isEvent) {
                rows.push(
                    <TableRow
                        key={`${date}-event-header`}
                        id={`scrollable-date-${date}`}
                        sx={{
                            backgroundColor: (theme) => theme.palette.background.default,
                            position: 'sticky',
                            top: 0
                        }}
                    >
                        <TableCell sx={{
                            ...dateCellSx, fontWeight: 500, width: 180,
                            maxWidth: 180,
                            minWidth: 180
                        }}>{formattedDate}</TableCell>
                        <TableCell sx={{ ...dateCellSx, fontSize: '11px' }}>PURPOSE</TableCell>
                    </TableRow>
                )

                rows.push(...filtered.map((entry, i) => renderEventRow(entry, date, i)))
            } else {
                const firstAction = filtered[0].data as CorporateAction
                rows.push(renderHeaderRowForActionType(firstAction.type, formattedDate))
                rows.push(...filtered.map((entry, i) => renderCorporateActionRow(entry.data as CorporateAction, date, i)))
            }
        }

        return hasRenderedEvents ? rows : <EmptyRow />
    }

    const renderHeaderRowForActionType = (type: Type, date: string) => {
        let headers: string[] = []

        switch (type) {
            case Type.BONUS:
                headers = [`${date}`, 'New FV', 'Bonus Ratio']
                break
            case Type.SPLIT:
                headers = [`${date}`, 'New FV', 'Split Ratio']
                break
            case Type.RIGHTS:
                headers = [`${date}`, 'New FV', 'Premium', 'Rights Ratio']
                break
            case Type.INTERIM_DIVIDEND:
            case Type.FINAL_DIVIDEND:
            case Type.SPECIAL_DIVIDEND:
                headers = [`${date}`, 'Type', 'Amount', 'Percentage']
                break
            default:
                headers = [`${date}`, 'Details']
        }

        return (
            <TableRow
                key={`${date}-header-${type}`}
                id={`scrollable-date-${date}`}
                sx={{ backgroundColor: (theme) => theme.palette.background.default, position: 'sticky', top: 0 }}
            >
                {headers.map((header, idx) => (
                    <TableCell
                        key={idx}
                        sx={{
                            ...dateCellSx, width: idx === 0 ? 180 : 'auto',
                            maxWidth: idx === 0 ? 180 : 'auto',
                            minWidth: idx === 0 ? 180 : 'auto', fontWeight: idx === 0 ? 500 : 'normal', textAlign: header === 'Type' ? 'center' : idx === 0 ? 'left' : 'right', fontSize: idx === 0 ? undefined : '11px'
                        }}
                    >
                        {idx === 0 ? date : header.toUpperCase()}
                    </TableCell>
                ))}
            </TableRow>
        )
    }

    const renderPrefixOrSuffix = (isCurrency: boolean) => (
        <Box
            component="span"
            sx={{
                color: (theme) => theme.palette.text.disabled,
                fontSize: '10px',
                display: 'inline',
                mx: '4px'
            }}
        >
            {isCurrency ? '₹' : '%'}
        </Box>
    )

    const renderCorporateActionRow = (action: CorporateAction, date: string, index: number) => {
        const symbolCell = <SymbolTableCell symbol={action.primaryCode} onClick={() => handleSymbolClick(action.primaryCode, action.primaryExchange)} exchange={action.primaryExchange} />
        const formatFixed = (value: number) => value.toFixed(2)
        switch (action.type) {
            case Type.BONUS:
                return (
                    <TableRow sx={{ ...tableRowProps }} key={`${date}-bonus-${index}`}>
                        {symbolCell}
                        <TableCell sx={{ ...tableCellProps, textAlign: 'right' }}>
                            {renderPrefixOrSuffix(true)}
                            {formatFixed(action.newFaceValue / 100)}
                        </TableCell>
                        <TableCell sx={{ ...tableCellProps, textAlign: 'right' }}>
                            {action.ratioNumerator}:{action.ratioDenominator}
                        </TableCell>
                    </TableRow>
                )
            case Type.SPLIT:
                return (
                    <TableRow sx={{ ...tableRowProps }} key={`${date}-split-${index}`}>
                        {symbolCell}
                        <TableCell sx={{ ...tableCellProps, textAlign: 'right' }}>
                            {renderPrefixOrSuffix(true)}
                            {formatFixed(action.newFaceValue / 100)}
                        </TableCell>
                        <TableCell sx={{ ...tableCellProps, textAlign: 'right' }}>
                            {action.ratioNumerator}:{action.ratioDenominator}
                        </TableCell>
                    </TableRow>
                )
            case Type.RIGHTS:
                return (
                    <TableRow sx={{ ...tableRowProps }} key={`${date}-rights-${index}`}>
                        {symbolCell}
                        <TableCell sx={{ ...tableCellProps, textAlign: 'right' }}>
                            {renderPrefixOrSuffix(true)}
                            {formatFixed(action.newFaceValue / 100)}
                        </TableCell>
                        <TableCell sx={{ ...tableCellProps, textAlign: 'right' }}>
                            {renderPrefixOrSuffix(true)}
                            {formatFixed(action.rightsPremium / 100)}
                        </TableCell>
                        <TableCell sx={{ ...tableCellProps, textAlign: 'right' }}>
                            {action.ratioNumerator}:{action.ratioDenominator}
                        </TableCell>
                    </TableRow>
                )
            case Type.INTERIM_DIVIDEND:
            case Type.FINAL_DIVIDEND:
            case Type.SPECIAL_DIVIDEND:
                return (
                    <TableRow sx={{ ...tableRowProps }} key={`${date}-dividend-${index}`}>
                        {symbolCell}
                        <TableCell sx={{ ...tableCellProps, textAlign: 'center' }}>{typeLabels[action.type]}</TableCell>
                        <TableCell sx={{ ...tableCellProps, textAlign: 'right' }}>
                            {renderPrefixOrSuffix(true)}
                            {formatFixed(action.dividendAmount / 100)}
                        </TableCell>
                        <TableCell sx={{ ...tableCellProps, textAlign: 'right' }}>
                            {formatFixed(action.dividendPercentage)}
                            {renderPrefixOrSuffix(false)}
                        </TableCell>
                    </TableRow>
                )
            default:
                return (
                    <TableRow sx={{ ...tableRowProps }} key={`${date}-unknown-${index}`}>
                        {symbolCell}
                        <TableCell sx={{ ...tableCellProps, textAlign: 'center' }}>{typeLabels[action.type]}</TableCell>
                    </TableRow>
                )
        }
    }

    const renderEventRow = (entry: any, date: string, index: number) => {
        const { primaryCode, details, primaryExchange, displayPurpose } = entry.data
        return (
            <TableRow sx={{ ...tableRowProps }} key={`${date}-event-${index}`}>
                <SymbolTableCell symbol={primaryCode} onClick={() => handleSymbolClick(primaryCode, primaryExchange)} exchange={primaryExchange} />
                <PurposeTableCell details={details} displayPurpose={displayPurpose} />
            </TableRow>
        )
    }

    const EmptyRow = () => (
        <TableRow>
            <TableCell colSpan={3} align="center">
                <Stack gap={2} justifyContent="center" alignItems="center" mt={4}>
                    <EmptySearch />
                    <Typography variant="body1">No Events Available</Typography>
                </Stack>
            </TableCell>
        </TableRow>
    )

    const WeekRangeComponent = useCallback(({ start, end, calendarData }: {
        start: Dayjs | null
        end: Dayjs | null
        calendarData?: { [key: string]: CompanyEventsAndActions }
    }) => {
        if (!start || !end) return null
        const dates: Dayjs[] = []
        for (
            let date = start;
            date.isBefore(end) || date.isSame(end, 'day');
            date = date.add(1, 'day')
        ) {
            dates.push(date)
        }
        return (
            <>
                {dates.map((date) => {
                    const isSelected = selectedDate?.isSame(date, 'day') ?? false
                    const isToday = date.isSame(dayjs(), 'day')
                    const categoryCounts = getTopCategoryCounts(calendarData?.[date.format('YYYY-MM-DD')])
                    return (
                        <Card
                            key={date.format('YYYY-MM-DD')}
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                minHeight: 132,
                                borderRadius: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                border: isSelected ? '0px' : '1px solid',
                                borderColor: isSelected ? 'none' : 'divider',
                                bgcolor: isSelected ? 'action.hover' : 'background.paper'
                            }}
                        >
                            <CardActionArea
                                onClick={() => scrollToSelectedDate(date)}
                                sx={{
                                    flex: 1,
                                    width: '100%',
                                    p: 1.5,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'stretch',
                                    justifyContent: 'flex-start'
                                }}
                            >
                                <Typography
                                    sx={{
                                        mb: 1 ,
                                        fontWeight: 500,
                                        textDecoration: isToday ? 'underline' : 'none',
                                        textUnderlineOffset: '6px',
                                        textDecorationThickness: '3px'
                                    }}
                                >
                                    {date.format('ddd DD')}
                                </Typography>
                                <Stack spacing={0.25} sx={{ width: '100%' }} >
                                    {categoryCounts.map(({ category, count }) => (
                                        <Stack
                                            key={category}
                                            direction="row"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            spacing={0.5}
                                        >
                                            <Typography
                                                color="text.secondary"
                                                noWrap
                                                sx={{ minWidth: 0, flex: 1, fontSize: '12px', lineHeight: 1.25, color: 'text.secondary' }}
                                            >
                                                {category}
                                            </Typography>
                                            <Typography fontWeight={400} sx={{ flexShrink: 0, fontSize: '12px', fontWeight: 400, lineHeight: 1.25 }}>
                                                {count}
                                            </Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                            </CardActionArea>
                        </Card>
                    )
                })}
            </>
        )
    }, [selectedDate])

    return (
        <div>
            <NonInteractiveTooltip arrow placement="left" title="Calendars" describeChild>
                <span>
                    <ExtraSmallButton
                        id="calendars-button"
                        aria-label="Calendars"
                        size="small"
                        color="inherit"
                        onClick={handleCalendarButtonClick}
                        data-event="Calendars Clicked"
                        sx={mergeSideBarBottomButtonSx(theme => ({
                            backgroundColor: open ? theme.palette.action.selected : 'transparent'
                        }))}
                    >
                        <CalendarIcon />
                    </ExtraSmallButton>
                </span>
            </NonInteractiveTooltip>

            <Drawer
                anchor="right"
                open={open}
                onClose={createOverlayDrawerOnClose(handleDrawerClose)}
                {...sideBarOverlayDrawerProps}
                PaperProps={sideBarOverlayDrawerPaperProps}
            >
                <Paper sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Stack gap={2} sx={{ flexShrink: 0 }}>
                        {/* Header */}
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Stack direction="row" spacing={1} alignItems="center">
                                <NonInteractiveTooltip title={'Open today'} arrow placement={'top'}>
                                    <Button onClick={() => scrollToCurrentWeekIfNeeded()} variant="outlined" color="inherit" sx={{ p: 0, height: 34 }}>
                                        Today
                                    </Button>
                                </NonInteractiveTooltip>
                                <Suspense fallback={null}>
                                    <WeekPicker onChange={handleWeekChange} isCurrentWeek={isCurrentWeek} />
                                </Suspense>
                            </Stack>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <NonInteractiveTooltip
                                    title={weekCardsExpanded ? 'Collapse header' : 'Expand header'}
                                    arrow
                                    placement="top"
                                >
                                    <Button
                                        variant="outlined"
                                        color="inherit"
                                        onClick={() =>
                                            dispatch({
                                                type: commonActionTypes.SET_CALENDAR_WEEK_CARDS_EXPANDED,
                                                payload: !weekCardsExpanded
                                            })
                                        }
                                        sx={{ minWidth: '34px', width: '34px', height: '34px', p: 0, borderRadius: '4px' }}
                                    >
                                        {weekCardsExpanded ? (
                                            <ThinArrowUpIcon sx={{ fontSize: '34px' }} />
                                        ) : (
                                            <ThinArrowDownIcon sx={{ fontSize: '34px' }} />
                                        )}
                                    </Button>
                                </NonInteractiveTooltip>
                                <ExtraSmallButton sx={{ height: '34px', width: '34px' }} onClick={() => toggleDrawer(false)} color="inherit">
                                    <ClearIcon sx={{ fontSize: '24px' }} />
                                </ExtraSmallButton>
                            </Stack>
                        </Stack>

                        <Collapse in={weekCardsExpanded}>
                            <Stack direction="row" spacing={1} alignItems="start" sx={{ width: '100%' }}>
                                {loading && weekRange ? (
                                    <CalendarWeekCardsSkeleton
                                        start={weekRange.start}
                                        end={weekRange.end}
                                        selectedDate={selectedDate}
                                    />
                                ) : (
                                    <WeekRangeComponent start={weekRange?.start} end={weekRange?.end} calendarData={data} />
                                )}
                            </Stack>
                        </Collapse>

                        {/* Filters */}
                        <Stack direction="row" flexWrap="wrap" rowGap={1}>
                            {displayedFilters
                                .filter((value) => value !== 'Others')
                                .map((value) => (
                                    <Chip
                                        key={value}
                                        label={value}
                                        clickable
                                        sx={{ mr: 1, fontWeight: 500 }}
                                        onClick={() => handleFilterChange(value)}
                                        variant={selectedFilter === value ? 'filled' : 'outlined'}
                                    />
                                ))}
                        </Stack>
                    </Stack>

                    {/* Event Table */}
                    {loading && (
                        <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: '100%' }}>
                            <CircularProgress />
                        </Box>
                    )}
                    <Box ref={tableRef} sx={{ flexGrow: 1, minHeight: 0, mt: 2 }}>
                        {!loading && (
                            <TableContainer
                                ref={tableContainerRef}
                                sx={{
                                    height: '100%',
                                    overflowY: 'auto',
                                    scrollbarWidth: 'none',
                                    '&::-webkit-scrollbar': { display: 'none' }
                                }}
                            >
                                <Table stickyHeader>
                                    <TableBody>{renderRows(data, selectedFilter)}</TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                </Paper>
            </Drawer>
        </div>
    )
}
